import React, { useState } from 'react';
import api from '../../../services/api';
import Modal from '../../../components/Modal';
import {
  getMeetingDisplayName,
  formatDuration,
  formatMeetingDateTime
} from '../utils/meetingTemplates';

/**
 * Modal för deltagare (byggare, IA, etc.) att svara på mötesförslag
 */
const AttendeeResponseModal = ({
  isOpen,
  onClose,
  meeting,
  userId,
  onResponseSubmitted
}) => {
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!meeting) return null;

  const proposals = meeting.proposalWorkflow?.proposals || [];

  // Hitta attendee för att få responseToken
  const attendee = meeting.attendees?.find(a =>
    a.userId === userId ||
    a.userId?._id === userId ||
    (a.userId && a.userId.toString() === userId)
  );

  const handleResponseChange = (proposalIndex, canAttend) => {
    setResponses(prev => ({
      ...prev,
      [proposalIndex]: canAttend
    }));
  };

  const handleSubmit = async () => {
    const responseArray = Object.entries(responses).map(([idx, canAttend]) => ({
      proposalIndex: parseInt(idx),
      canAttend
    }));

    // Kolla att alla förslag har svar
    if (responseArray.length !== proposals.length) {
      setError('Vänligen markera alla föreslagna tider');
      return;
    }

    if (!attendee?.responseToken) {
      setError('Kunde inte hitta svarstoken. Kontakta administratören.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await api.post(`/meetings/${meeting._id}/respond`, {
        token: attendee.responseToken,
        responses: responseArray
      });

      onResponseSubmitted?.();
    } catch (err) {
      console.error('Fel vid svar:', err);
      setError(err.response?.data?.message || 'Kunde inte skicka svar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="small">
          {/* Header */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Svara på tidsförslag
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {meeting.displayName || getMeetingDisplayName(meeting)}
            </p>
          </div>

          {/* Mötesinfo */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {formatDuration(meeting.duration)}
            </div>
            {meeting.location && (
              <div className="flex items-center text-sm text-gray-600">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {meeting.location.displayName}
                {meeting.location.address && (
                  <span className="text-gray-400 ml-1">- {meeting.location.address}</span>
                )}
              </div>
            )}
          </div>

          {/* Instruktion */}
          <p className="text-sm text-gray-600 mb-4">
            Markera vilka tider som passar dig:
          </p>

          {/* Föreslagna tider */}
          <div className="space-y-3 mb-4">
            {proposals.map((proposal, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border"
              >
                <span className="text-sm font-medium text-gray-700">
                  {proposal.displayText || formatMeetingDateTime(proposal.date, proposal.time)}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleResponseChange(idx, true)}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                      responses[idx] === true
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-green-100'
                    }`}
                  >
                    Kan
                  </button>
                  <button
                    onClick={() => handleResponseChange(idx, false)}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                      responses[idx] === false
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-red-100'
                    }`}
                  >
                    Kan inte
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Felmeddelande */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Knappar */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Avbryt
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(responses).length !== proposals.length}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {submitting ? 'Skickar...' : 'Skicka svar'}
            </button>
          </div>
    </Modal>
  );
};

export default AttendeeResponseModal;
